import { API_ENDPOINTS } from "@/constants/api";
import { http } from "@/lib/http-client";
import { unwrapData } from "@/lib/api-response";
import type {
  ListNotificationsQuery,
  NotificationPreferences,
  UnreadCountResponse,
  UserNotification,
} from "@/types/notification";
import type { PaginatedResult } from "@/types/common";

interface NotificationPagination {
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
}

const EMPTY_META = { total: 0, page: 1, limit: 25, totalPages: 1 };

export const notificationService = {
  async listInbox(
    query?: ListNotificationsQuery,
  ): Promise<PaginatedResult<UserNotification>> {
    const response = await http.get(API_ENDPOINTS.NOTIFICATIONS.LIST, {
      params: query,
    });
    const data = unwrapData<{
      items?: UserNotification[];
      pagination?: NotificationPagination;
    }>(response);
    const items = Array.isArray(data?.items) ? data.items : [];
    const pagination = data?.pagination;
    return {
      items,
      meta: pagination
        ? {
            total: pagination.total,
            page: pagination.page,
            limit: pagination.pageSize,
            totalPages: pagination.pageCount,
          }
        : EMPTY_META,
    };
  },

  async getUnreadCount(): Promise<number> {
    const response = await http.get(API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT);
    const data = unwrapData<UnreadCountResponse>(response);
    return data?.count ?? 0;
  },

  async getDetail(id: string): Promise<UserNotification> {
    const response = await http.get(API_ENDPOINTS.NOTIFICATIONS.DETAIL(id));
    return unwrapData<UserNotification>(response);
  },

  async markRead(id: string): Promise<void> {
    await http.patch(API_ENDPOINTS.NOTIFICATIONS.MARK_READ(id));
  },

  async markAllRead(): Promise<void> {
    await http.patch(API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ);
  },

  async getPreferences(): Promise<NotificationPreferences> {
    const response = await http.get(API_ENDPOINTS.NOTIFICATIONS.PREFERENCES);
    return unwrapData<NotificationPreferences>(response);
  },

  async updatePreferences(
    payload: NotificationPreferences,
  ): Promise<NotificationPreferences> {
    const response = await http.put(
      API_ENDPOINTS.NOTIFICATIONS.PREFERENCES,
      payload,
    );
    return unwrapData<NotificationPreferences>(response);
  },
};
